import React from 'react';
import { compact, noop } from 'lodash';
import { PropTypes, describe } from 'react-desc';
import ArrowheadRight from '@elliemae/ds-icons/ArrowheadRight';
import ArrowheadDown from '@elliemae/ds-icons/ArrowheadDown';
import { useTheme } from '@elliemae/ds-system';
import Button from '@elliemae/ds-button';
import DSSeparator from '@elliemae/ds-separator';
import { Grid } from '@elliemae/ds-grid';
import DSCheckbox from '@elliemae/ds-form/Checkbox';
import { Container, Title, Description, RightDescription, RightValue, RightAddon } from './styled.js';
import 'styled-components';

/* eslint-disable max-lines */

var DetailCard = function DetailCard(_ref) {
  var title = _ref.title,
      description = _ref.description,
      _ref$descriptionColor = _ref.descriptionColor,
      descriptionColor = _ref$descriptionColor === void 0 ? 'neutral' : _ref$descriptionColor,
      rightValue = _ref.rightValue,
      rightDescription = _ref.rightDescription,
      rightAddon = _ref.rightAddon,
      _ref$selectable = _ref.selectable,
      selectable = _ref$selectable === void 0 ? false : _ref$selectable,
      _ref$isSelected = _ref.isSelected,
      isSelected = _ref$isSelected === void 0 ? false : _ref$isSelected,
      _ref$onSelect = _ref.onSelect,
      onSelect = _ref$onSelect === void 0 ? noop : _ref$onSelect,
      _ref$expandable = _ref.expandable,
      expandable = _ref$expandable === void 0 ? false : _ref$expandable,
      _ref$isExpanded = _ref.isExpanded,
      isExpanded = _ref$isExpanded === void 0 ? false : _ref$isExpanded,
      _ref$onExpand = _ref.onExpand,
      onExpand = _ref$onExpand === void 0 ? noop : _ref$onExpand,
      expandContent = _ref.expandContent,
      _ref$readOnly = _ref.readOnly,
      readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled;
  var theme = useTheme();
  var hasRightLegend = rightValue && rightDescription;
  var hasRightPosition = hasRightLegend || rightAddon;
  var topWrapperCols = ['1fr'];
  if (hasRightPosition) topWrapperCols.push('auto');
  if (expandable) topWrapperCols.unshift('20px');
  if (selectable) topWrapperCols.unshift(theme.space.s);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Container, {
    disabled: disabled || readOnly,
    active: selectable && isSelected,
    "data-testid": "card-detail-container"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: topWrapperCols,
    alignItems: !description && 'center'
  }, selectable && /*#__PURE__*/React.createElement(Grid, {
    pt: description && '9px'
  }, /*#__PURE__*/React.createElement(DSCheckbox, {
    checked: isSelected,
    onChange: onSelect,
    readOnly: readOnly,
    disabled: disabled
  })), expandable && /*#__PURE__*/React.createElement(Grid, {
    height: "16px",
    pt: description && 'xxs',
    mt: !description && '-5px'
  }, /*#__PURE__*/React.createElement(Button, {
    size: "s",
    buttonType: "text",
    onClick: onExpand,
    icon: isExpanded ? /*#__PURE__*/React.createElement(ArrowheadDown, {
      color: ['neutral', 500],
      size: "s"
    }) : /*#__PURE__*/React.createElement(ArrowheadRight, {
      color: ['neutral', 500],
      size: "s"
    }),
    disabled: disabled || readOnly
  })), /*#__PURE__*/React.createElement(Grid, {
    pr: "24px"
  }, /*#__PURE__*/React.createElement(Title, null, title), description && /*#__PURE__*/React.createElement(Description, {
    descriptionColor: descriptionColor
  }, description)), hasRightPosition && /*#__PURE__*/React.createElement(Grid, {
    cols: compact([hasRightPosition && 'auto', rightAddon && 'auto'])
  }, hasRightLegend && /*#__PURE__*/React.createElement(Grid, {
    pr: "24px",
    rows: ['auto', 'auto', 1]
  }, /*#__PURE__*/React.createElement(RightDescription, {
    justifyContent: "flex-end"
  }, rightDescription), /*#__PURE__*/React.createElement(RightValue, {
    justifyContent: "flex-end"
  }, rightValue), /*#__PURE__*/React.createElement(Grid, null)), rightAddon && /*#__PURE__*/React.createElement(RightAddon, {
    cols: ['auto', 'auto'],
    gutter: "2px"
  }, rightAddon))), expandContent && isExpanded && /*#__PURE__*/React.createElement(Grid, {
    ml: selectable && 's',
    mr: rightAddon && 'xs'
  }, /*#__PURE__*/React.createElement(DSSeparator, {
    dashed: true,
    position: "initial",
    margin: "none"
  }), /*#__PURE__*/React.createElement(Grid, {
    pt: "xxs"
  }, expandContent))));
};

var detailProps = {
  /**
   * Title of the card.
   */
  title: PropTypes.string.isRequired.description('Title of the card.'),

  /**
   * Description of the card.
   */
  description: PropTypes.string.description('Description of the card.'),

  /**
   * Description color.
   */
  descriptionColor: PropTypes.oneOf(['primary', 'neutral']).description('Description color.'),

  /**
   * Right value (should be used with `rightDescription`)
   */
  rightValue: PropTypes.string.description('Right value (should be used with `rightDescription`)'),

  /**
   * Right description (should be used with `rightValue`)
   */
  rightDescription: PropTypes.string.description('Right description (should be used with `rightValue`)'),

  /**
   * Right addon array, max elements: 2
   */
  rightAddon: PropTypes.element.description('Right addon array, max elements: 2'),

  /**
   * Whether if the card is selectable or not
   */
  selectable: PropTypes.bool.description('Whether if the card is selectable or not'),

  /**
   * Whether if the card is selected or not
   */
  isSelected: PropTypes.bool.description('Whether if the card is selected or not'),

  /**
   * Callback on selection
   */
  onSelect: PropTypes.func.description('Callback on selection'),

  /**
   * Whether if the card is expandable or not
   */
  expandable: PropTypes.bool.description('Whether if the card is expandable or not'),

  /**
   * Whether if the card is expanded or not
   */
  isExpanded: PropTypes.bool.description('Whether if the card is expanded or not'),

  /**
   * Callback on expand
   */
  onExpand: PropTypes.func.description('Callback on expand'),

  /**
   * Content
   */
  expandContent: PropTypes.element.description('Content'),

  /**
   * Read only
   */
  readOnly: PropTypes.bool.description('Read only'),

  /**
   * Disabled
   */
  disabled: PropTypes.bool.description('Disabled')
};
DetailCard.propTypes = detailProps;
var DSCardDetailWithSchema = describe(DetailCard);
DSCardDetailWithSchema.propTypes = detailProps;

export default DetailCard;
export { DSCardDetailWithSchema, DetailCard };
//# sourceMappingURL=DetailCard.js.map
